import React, {
	useMemo,
	useState,
	useRef,
	useEffect,
	memo,
} from 'react';
import {
	useDispatch,
	useSelector,
} from 'react-redux';
import classNames from 'classnames';
import Socket from '../../connection/socket';
import { GreenroomState as GS } from '../../stores/types/types';
import {
	MediaItem,
	PresentedMediaItem,
} from '../../stores/reducers/media';
import useMedia from '../../hooks/use-media';
import useEchoCancellation, {
	shouldDoEchoCancel,
} from '../../hooks/use-echo-cancellation';
import Portal from '../general-components/portal';
import { videoGridClasses } from '../../types/constants';
import {
	DRAG_TYPE,
	DropCallback,
} from '../../stores/reducers/user-interface';
import FeedMediaContextMenu from './feed-media-context-menu';
import { setOpenContextMenuId } from '../../stores/actions/user-interface';

import { GeneratedContent } from '../../stores/reducers/generate-content';
import VideoHoverOptions from './video-hover-options';
import { STATIC_ASSET_URL } from '../../utils/content-templates';
import MediaControlsUi from './media-controls-ui';
import useDropArea from '../../hooks/use-dnd/use-drop-area';
import { isAudio, isVideo } from '../../utils/utils';
import V_LAYOUTS from '../../utils/video-layout';
import useShots from '../../hooks/use-shots';

import {
	connectorIdEditing, connectorIdLive, projectedComponentIdEditing, projectedComponentIdLive,
} from '../spotlight/spotlight-utils';
import SpotlightConnector from '../spotlight/spotlight-connector';
import MediaVideoSpotlight from '../general-components/media-video-spotlight';
import MediaAudioSpotlight from '../general-components/media-audio-spotlight';

interface Props {
	mediaId: number;
	feedPosition: number;
	socket: Socket | null;
	selectedMediaFeed?: number;
	onSelectMediaFeedChange?: React.Dispatch<React.SetStateAction<number | undefined>>;
	isForMainVideoEditingComponent?: boolean;
	iterateLoadedMediaCount: Function;
	isEditingFeed: boolean;
	isForShotEditorModal?: boolean;
	dropCallback: DropCallback;
	audioOnly?: boolean;
	showbuilderMediaItem?: PresentedMediaItem;
	updateShowbuilderMedia?: Function;
	showBackground?: boolean;
	notVisible?: boolean;
}

const doEchoCancel = shouldDoEchoCancel();

export default memo((props: Props) => {
	const {
		mediaId,
		feedPosition,
		socket,
		isForMainVideoEditingComponent = false,
		iterateLoadedMediaCount,
		isEditingFeed = false,
		dropCallback,
		audioOnly,
		showbuilderMediaItem,
		updateShowbuilderMedia = () => {},
		showBackground,
		notVisible,
	} = props;

	const isForShotEditorModal = props.isForShotEditorModal ? props.isForShotEditorModal : false;
	const host = useSelector((state: GS) => state.SessionReducer.host);
	const user = useSelector((state: GS) => state.SessionReducer.user);
	const roomState = useSelector((state: GS) => state.MeetingReducer.roomState);
	const media = useSelector((state: GS) => state.MediaReducer.media);
	const mediaPages = useSelector((state: GS) => state.MediaReducer.mediaPages);
	const presentedMedia = useSelector((state: GS) => state.MediaReducer.presentedMedia);
	const globalIsDragging = useSelector(
		(state: GS) => state.UserInterfaceReducer.globalIsDragging,
	);
	const modalOpen = useSelector((state: GS) => state.ModalReducer.modalOpen);
	const generatedContent = useSelector((s: GS) => s.GeneratedContentReducer.generatedContent);
	const contentTemplates = useSelector((s: GS) => s.GeneratedContentReducer.contentTemplates);
	const {
		currentShot,
	} = useShots();
	const {
		canEditOrControlMedia,
	} = useMedia();

	const {
		shot_data: {
			forcedLayout,
		},
	} = currentShot;

	// const [showBackground, setShowBackground] = useState<boolean>(false);
	const [isHovering, setIsHovering] = useState<boolean>(false);
	const [rightClickCoords, setRightClickCoords] = useState<[number, number]>();

	const dispatch = useDispatch();
	const videoElRef = useRef<HTMLVideoElement>(null);
	const audioElRef = useRef<HTMLAudioElement>(null);
	const echoCancelAudioRef = useRef<HTMLAudioElement>(null);

	const timeBubbleRef = useRef<HTMLInputElement>(null);

	// used to sync the start time of new videos with paginated media

	// FeedMedia is used for media being presented as well as media in Showbuilder which is why we use
	// both of these variables
	const mediaItem = useMemo(
		() => media.find((m: MediaItem) => m.id === mediaId),
		[mediaId, media],
	);
	const isHiddenTwoUpShot = useMemo(
		(): boolean => feedPosition === 0
			&& forcedLayout === V_LAYOUTS.TWO.FULL_TWO,
		[forcedLayout, feedPosition],
	);

	// does this media item have an associated generated content item?
	const mediaGeneratedContent = useMemo(() => {
		if (!generatedContent) {
			return undefined;
		}

		return generatedContent.find((t: GeneratedContent) => t.fkId === mediaId);
	}, [mediaId, generatedContent]);
	const mediaGeneratedContentTemplate = useMemo(() => {
		if (mediaGeneratedContent) {
			return contentTemplates.find((t) => t.id === mediaGeneratedContent.templateId);
		}

		if (mediaItem) {
			// non-edited media items image will match a template
			return contentTemplates.find((t) => t.videoUrl
				?.replace('ASSET_URL', STATIC_ASSET_URL ?? '') === mediaItem?.images);
		}
		return null;
	}, [mediaGeneratedContent, mediaItem]);
	useEchoCancellation(videoElRef, echoCancelAudioRef);

	const presentedMediaItem = useMemo(
		() => (isEditingFeed ? showbuilderMediaItem : presentedMedia
			.find((m) => m.mediaId === mediaId)),
		[presentedMedia, mediaId, showbuilderMediaItem, isEditingFeed],
	);

	const urlOfCurrentPage = useMemo(() => {
		const currentPage = presentedMediaItem?.currentPage ?? 1;
		const imageArray = mediaItem?.media_data.image_array ?? [''];
		return imageArray[currentPage - 1] ?? '';
	}, [
		mediaItem?.media_data.image_array,
		presentedMediaItem?.currentPage,
	]);

	const isDeck = useMemo(() => {
		if (!mediaItem || !mediaItem.num_pages) return false;
		return mediaItem.num_pages > 1;
	}, [mediaItem?.num_pages]);

	const { isOver, dropRef } = useDropArea({
		accepts: DRAG_TYPE.FEEDS,
		dropCallback,
		dropAreaData: { feedPosition },
	});
	const currentPageIsVideo = useMemo(() => isVideo(urlOfCurrentPage), [urlOfCurrentPage]);
	const currentPageIsAudio = useMemo(() => isAudio(urlOfCurrentPage), [urlOfCurrentPage]);

	// The right click context menu requires a unique string id
	const contextMenuId = useMemo(() => `feed-media-${mediaItem?.id}`, [mediaItem]);

	return (
		<>
			{isOver && <div className="dnd-highlighted" />}
					<div
						key={feedPosition}
						className={classNames('media', mediaItem?.media_data.fill && 'fill')}
						data-testid='videoMediaControls'
						ref={dropRef}
						style={{
							background: showBackground ? mediaItem?.media_data.background_color : undefined,
						}}
						onMouseEnter={() => setIsHovering(true)}
						onMouseLeave={() => setIsHovering(false)}
						onContextMenu={(e) => {
							if (host) {
								e.preventDefault();
								setRightClickCoords([e.pageX, e.pageY]);
								dispatch(setOpenContextMenuId(contextMenuId));
							}
						}}
					>
						{
						mediaItem?.id
						&& user
						&& (!modalOpen || isForShotEditorModal)
						&& (
							// This portal is to work around a `transform: translateZ(0);` hack on .main-video
							// (the transform makes fixed children elements like the custom context menu fixed
							// relative to it rather than the viewport)
							<Portal targetId="feed-media-context-menu-portal">
								<FeedMediaContextMenu
									mediaItem={mediaItem}
									contextMenuId={contextMenuId}
									clickCoordinates={rightClickCoords}
									socket={socket}
									user={user}
									canEditMedia={canEditOrControlMedia(mediaItem, user, host, false)}
								/>
							</Portal>
						)}
						{
							<div className="flex-center width-full height-full" id={`feed-${feedPosition}`}>
								{/* {currentPageIsVideo && (
									<>
										<SpotlightConnector
											uniqueSpotlightId={idOfConnector}
											desiredContainerId={idOfVideoContainer}
											mediaId={mediaId}
										>
											<MediaVideoSpotlight
												id={`media-video-${mediaId}-${feedPosition}${audioOnly ? '-audio-only' : ''}`}
												key={`${'key_FeedMedia_'}${mediaId}${feedPosition}${audioOnly ? '-audio-only' : ''}`}
												className="media-video"
												crossOrigin={'anonymous'}
												mediaId={mediaId}
												iterateLoadedMediaCount={iterateLoadedMediaCount}
												socket={socket}
												// onCanPlayThrough={() => setShowBackground(true)}
												loop={!!mediaItem?.media_data.loop}
												isPreloading={false}
												doEchoCancel={doEchoCancel}
												audioOnly={audioOnly}
												isEditingFeed={isEditingFeed}
												showbuilderMediaItem={showbuilderMediaItem}
												isForMainVideoEditingComponent={isForMainVideoEditingComponent}
											/>
											{doEchoCancel
												? <audio
													id={`media-audio-${mediaId}-${feedPosition}${audioOnly ? '-audio-only' : ''}`}
													ref={echoCancelAudioRef}
												></audio>
												: <></>
											}
										</SpotlightConnector>
									</>
								)} */}

								{currentPageIsAudio && (
									<>
										{/* <MediaAudioSpotlight
											id={`media-video-${mediaId}-${feedPosition}${audioOnly ? '-audio-only' : ''}`}
											key={`${'key_FeedMedia_'}${mediaId}${feedPosition}${audioOnly ? '-audio-only' : ''}`}
											mediaItem={mediaItem}
											loop={!!mediaItem?.media_data.loop}
											iterateLoadedMediaCount={iterateLoadedMediaCount}
											presentedMediaItem={presentedMediaItem}
											socket={socket}
											mediaId={mediaId}
											isPreloading={false}
											isEditingFeed={isEditingFeed}
										/>
										{doEchoCancel
											? <audio
												id={`media-audio-${mediaId}-${feedPosition}${audioOnly ? '-audio-only' : ''}`}
												ref={echoCancelAudioRef}
											></audio>
											: <></>
										} */}
									</>
								)}

								{!currentPageIsVideo && !currentPageIsAudio && (
									<img
										className="media-img"
										src={urlOfCurrentPage ?? ''}
										alt={''}
										onLoad={() => {
											iterateLoadedMediaCount();
										}}
									/>
								)}
							</div >
						}
					</div>
			{
				!notVisible
				&& mediaItem
				&& !globalIsDragging
				&& canEditOrControlMedia(mediaItem, user, host, true, roomState)
				&& <Portal
					targetId={isForMainVideoEditingComponent ? 'controls-container-editing' : 'controls-container-live'}
					className={classNames(
						`media-controls-layer width-full height-full ${videoGridClasses[feedPosition]}`,
						{
							hover: isHovering,
							'hidden-feed': isHiddenTwoUpShot,
							'display-none': audioOnly,
						},
					)}
				>
					<div
						className="flex width-full"
						onMouseEnter={() => setIsHovering(true)}
						onMouseLeave={() => setIsHovering(false)}
					>
						{currentPageIsVideo
							&& <MediaControlsUi
								isEditingFeed={isEditingFeed}
								showbuilderMediaItem={showbuilderMediaItem}
								mediaId={mediaId}
								isForShotEditorModal={isForShotEditorModal}
								host={host}
								isDeck={isDeck}
								timeBubbleRef={timeBubbleRef}
								updateShowbuilderMedia={updateShowbuilderMedia}/>
					}
						{mediaGeneratedContentTemplate && host && (
							<Portal targetId="video-hover-options-portal">
								<VideoHoverOptions
									mediaItem={mediaItem}
									generateContentTemplate={mediaGeneratedContentTemplate}
									generateContent={mediaGeneratedContent}
									className={classNames(
										'width-full',
										'height-full',
										videoGridClasses[feedPosition],
										{
											hover: isHovering,
											'hidden-feed': isHiddenTwoUpShot,
											'display-none': audioOnly,
										},
									)}
									videoRef={videoElRef.current}
								/>
							</Portal>
						)}
					</div>
				</Portal>
			}
		</>
	);
});
